import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LayoutDashboard, Users, Package, Flag, Settings, CreditCard, ChevronLeft, Shield, Zap, Megaphone, Briefcase, ShieldCheck, Newspaper, Activity } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/admin', label: 'Overview', icon: LayoutDashboard, exact: true },
  { path: '/admin/analytics', label: 'Analytics', icon: Activity },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/listings', label: 'Listings', icon: Package },
  { path: '/admin/promotions', label: 'Promotions', icon: Megaphone },
  { path: '/admin/promotion-tiers', label: 'Promotion Tiers', icon: Zap },
  { path: '/admin/business-ads', label: 'Business Ads', icon: Newspaper },
  { path: '/admin/business-accounts', label: 'Business', icon: Briefcase },
  { path: '/admin/verifications', label: 'Verifications', icon: ShieldCheck },
  { path: '/admin/reports', label: 'Reports', icon: Flag },
  { path: '/admin/transactions', label: 'Transactions', icon: CreditCard },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/auth'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
  }, [user, loading, navigate]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-zinc-900 min-h-screen flex-shrink-0 hidden lg:flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/" className="flex items-center gap-2 text-white mb-1">
            <Shield className="w-5 h-5 text-[#002FA7]" />
            <span className="text-lg font-black tracking-tighter">Admin Panel</span>
          </Link>
          <p className="text-xs text-zinc-500">Durham Market</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-[#002FA7] text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <Link to="/" className="flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition-colors">
            <ChevronLeft className="w-3 h-3" /> Back to Marketplace
          </Link>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-900 border-b border-zinc-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Shield className="w-4 h-4 text-[#002FA7]" />
            <span className="font-bold text-sm">Admin</span>
          </div>
          <Link to="/" className="text-xs text-zinc-400">Back</Link>
        </div>
        <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
          {NAV_ITEMS.map(item => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
            return (
              <Link key={item.path} to={item.path}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${isActive ? 'bg-[#002FA7] text-white' : 'text-zinc-400 bg-zinc-800'}`}
              >{item.label}</Link>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 lg:p-8 p-4 pt-24 lg:pt-8 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
